/**
 * Direct Hyperliquid API client for the Mini App.
 * Fetches portfolio equity and positions directly from Hyperliquid in the user's browser,
 * bypassing the server for faster, more responsive updates.
 * Includes core (mainnet) perps plus HIP-3 perp-dex markets (xyz = USDC, cash = USDT).
 */

import { isAdminRigDiagnosticsEnabled } from '../utils/adminRigDiagnostics';
import { miniappFetch } from './miniappApiClient';
import { HIP3_DEXS, hlInfoPost, isHlInfoRateLimited } from './hyperliquidDirectHlInfo';
import {
  applyLiveMarksToPortfolio,
  ingestHlAllMids,
  lookupPositionMark,
} from './livePositionMark';
import {
  computePnlSummaryFromFills,
  fetchHLUserFillsByTime,
  fetchHLWalletFillsSummary,
  type HLUserFill,
  type ParsedPnlSummary,
} from './hyperliquidDirectFills';
import {
  fetchHLLeaderboard,
  type HLLeaderboardPerformance,
  type HLLeaderboardSortWindow,
  type HLLeaderboardTrader,
} from './hyperliquidDirectLeaderboard';

export { HIP3_DEXS, isHlInfoRateLimited };
export {
  computePnlSummaryFromFills,
  fetchHLUserFillsByTime,
  fetchHLWalletFillsSummary,
  fetchHLLeaderboard,
  type HLLeaderboardPerformance,
  type HLLeaderboardSortWindow,
  type HLLeaderboardTrader,
  type HLUserFill,
  type ParsedPnlSummary,
};

// ─────────────────────────────────────────────────────────────
// Candle snapshots (price history) - browser-side (to reduce server load)
// ─────────────────────────────────────────────────────────────

export type HLCandleInterval =
  | '1m' | '3m' | '5m' | '15m' | '30m'
  | '1h' | '2h' | '4h' | '8h' | '12h'
  | '1d' | '3d' | '1w' | '1M';

export interface HLCandlePoint {
  time: number; // ms
  open: number;
  high: number;
  low: number;
  close: number;
  /** Base-asset volume when available (HL `v` / server `volume`). */
  volume?: number;
}

type HLMetaCacheEntry = { ts: number; map: Map<string, string> };
const HL_META_TTL_MS = 10 * 60_000;
const hlMetaCache = new Map<string, HLMetaCacheEntry>();
const hlMetaInFlight = new Map<string, Promise<Map<string, string>>>();

async function fetchHLMetaMap(opts?: { dex?: string }): Promise<Map<string, string>> {
  const dex = String(opts?.dex || '').trim().toLowerCase();
  const cacheKey = `meta:${dex || 'core'}`;
  const cached = hlMetaCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < HL_META_TTL_MS) return cached.map;

  const inflight = hlMetaInFlight.get(cacheKey);
  if (inflight) return inflight;

  const p = (async (): Promise<Map<string, string>> => {
    let data: any = null;
    try {
      data = await hlInfoPost(dex ? { type: 'meta', dex } : { type: 'meta' });
    } catch {
      return cached?.map || new Map();
    }
    const universe = data?.universe;

    const map = new Map<string, string>();
    if (Array.isArray(universe)) {
      for (const item of universe) {
        const name = String((item as any)?.name || '').trim();
        if (!name) continue;
        map.set(name.toUpperCase(), name);
      }
    }

    hlMetaCache.set(cacheKey, { ts: Date.now(), map });
    return map;
  })();

  hlMetaInFlight.set(cacheKey, p);
  try {
    return await p;
  } finally {
    hlMetaInFlight.delete(cacheKey);
  }
}

async function resolveHlCoinName(coin: string, opts?: { dex?: string }): Promise<string> {
  const raw = String(coin || '').trim();
  if (!raw) return raw;
  const upper = raw.toUpperCase();
  try {
    const map = await fetchHLMetaMap(opts);
    return map.get(upper) || raw;
  } catch {
    return raw;
  }
}

function parsePairForCandleSnapshot(pair: string): { coin: string; dex?: string } {
  const p = String(pair || '').trim();
  if (!p) return { coin: '' };
  const colonIdx = p.indexOf(':');
  if (colonIdx > 0) {
    const dex = p.slice(0, colonIdx).trim().toLowerCase();
    const coin = p.slice(colonIdx + 1).trim();
    if (dex && coin) return { coin, dex };
  }

  const u = p.toUpperCase();

  // HIP-3: BASE-QUOTE with quote in USDC (xyz), USDT (cash), etc. Main perps use USD.
  const hip3Quotes = ['USDC', 'USDH', 'USDE', 'USDT0', 'USDT'];
  const parts = u.split('-', 2);
  const quote = (parts[1] || '').trim();
  const isHip3 = u.includes('-') && quote !== 'USD' && hip3Quotes.includes(quote);

  if (isHip3) {
    const base = u.split('-')[0] || u;
    // Resolve dex from quote: USDT/USDT0 -> cash, USDC -> xyz
    const dex = (quote === 'USDT' || quote === 'USDT0') ? 'cash' : 'xyz';
    return { coin: base, dex };
  }

  // Default: treat anything like BTC-USD, BTC/USD, BTC as BTC.
  const base = u.split(/[-/]/)[0] || u;
  return { coin: base };
}

const HL_CANDLES_TTL_MS = 25_000;
/** Stale candles may be served up to this age when HL returns 429 or transient errors. */
const HL_CANDLES_STALE_MAX_MS = 5 * 60_000;
const hlCandleCache = new Map<string, { ts: number; data: HLCandlePoint[] }>();
const hlCandleInFlight = new Map<string, Promise<HLCandlePoint[]>>();

function readStaleHlCandles(cacheKey: string): HLCandlePoint[] | null {
  try {
    const mem = hlCandleCache.get(cacheKey);
    if (mem?.data?.length && Date.now() - mem.ts < HL_CANDLES_STALE_MAX_MS) return mem.data;
  } catch {}
  try {
    const ssKey = `ma_hl_candles_${cacheKey}`;
    const raw = sessionStorage.getItem(ssKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ts = Number(parsed?.ts || 0);
    const data = Array.isArray(parsed?.data) ? (parsed.data as HLCandlePoint[]) : null;
    if (data?.length && ts && Date.now() - ts < HL_CANDLES_STALE_MAX_MS) {
      hlCandleCache.set(cacheKey, { ts, data });
      return data;
    }
  } catch {}
  return null;
}

const HL_FUNDING_TTL_MS = 30_000;
const hlFundingCache = new Map<string, { ts: number; map: Map<string, number> }>();

export async function fetchHLCandleSnapshot(opts: {
  pair: string;
  interval: HLCandleInterval;
  startTimeMs: number;
  endTimeMs: number;
  /** Optional: cap on returned candles (HL supports this in some contexts; safe to omit). */
  limit?: number;
}): Promise<HLCandlePoint[]> {
  const pairRaw = String(opts.pair || '').trim();
  const interval = String(opts.interval || '').trim() as HLCandleInterval;
  const startTime = Number(opts.startTimeMs || 0);
  const endTime = Number(opts.endTimeMs || 0);
  if (!pairRaw || !interval || !Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) return [];

  const parsed = parsePairForCandleSnapshot(pairRaw);
  const dex = String(parsed.dex || '').trim().toLowerCase();
  const cacheKey = `candles:${dex || 'core'}:${pairRaw.toUpperCase()}:${interval}:${startTime}:${endTime}:${opts.limit || ''}`;

  try {
    const cached = hlCandleCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < HL_CANDLES_TTL_MS) return cached.data;
  } catch {}

  const inflight = hlCandleInFlight.get(cacheKey);
  if (inflight) return inflight;

  const p = (async () => {
    // sessionStorage cache (survives page transitions)
    try {
      const ssKey = `ma_hl_candles_${cacheKey}`;
      const raw = sessionStorage.getItem(ssKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        const ts = Number(parsed?.ts || 0);
        const data = Array.isArray(parsed?.data) ? (parsed.data as HLCandlePoint[]) : null;
        if (data && ts && Date.now() - ts < HL_CANDLES_TTL_MS) {
          hlCandleCache.set(cacheKey, { ts, data });
          return data;
        }
      }
    } catch {}

    try {
      const coinResolved = await resolveHlCoinName(parsed.coin, dex ? { dex } : undefined);
      // HIP-3: coin must be prefixed with dex name, e.g. "xyz:SILVER" (see Hyperliquid API docs)
      const coinForReq = dex ? `${dex}:${coinResolved}` : coinResolved;
      const body: any = {
        type: 'candleSnapshot',
        req: {
          coin: coinForReq,
          interval,
          startTime,
          endTime,
        },
      };
      if (Number.isFinite(Number(opts.limit)) && Number(opts.limit) > 0) body.req.limit = Number(opts.limit);

      const rows = await hlInfoPost<any>(body);
      const list = Array.isArray(rows) ? rows : Array.isArray((rows as any)?.candles) ? (rows as any).candles : [];
      const candles: HLCandlePoint[] = (Array.isArray(list) ? list : [])
        .map((c: any) => {
          const rawTime = Number(c?.t);
          const time = Number.isFinite(rawTime) ? (rawTime < 1_000_000_000_000 ? rawTime * 1000 : rawTime) : NaN;
          const volRaw = Number.parseFloat(String(c?.v ?? '0'));
          return {
            time,
            open: Number.parseFloat(String(c?.o ?? '')),
            high: Number.parseFloat(String(c?.h ?? '')),
            low: Number.parseFloat(String(c?.l ?? '')),
            close: Number.parseFloat(String(c?.c ?? '')),
            volume: Number.isFinite(volRaw) && volRaw >= 0 ? volRaw : 0,
          };
        })
        .filter((c: HLCandlePoint) =>
          Number.isFinite(c.time) &&
          Number.isFinite(c.open) &&
          Number.isFinite(c.high) &&
          Number.isFinite(c.low) &&
          Number.isFinite(c.close)
        )
        .sort((a, b) => a.time - b.time);

      hlCandleCache.set(cacheKey, { ts: Date.now(), data: candles });
      try {
        const ssKey = `ma_hl_candles_${cacheKey}`;
        sessionStorage.setItem(ssKey, JSON.stringify({ ts: Date.now(), data: candles }));
      } catch {}
      return candles;
    } catch (err) {
      console.warn('[HL Direct] candleSnapshot fetch error:', err);
      const stale = readStaleHlCandles(cacheKey);
      if (stale?.length) return stale;
      return [];
    } finally {
      hlCandleInFlight.delete(cacheKey);
    }
  })();

  hlCandleInFlight.set(cacheKey, p);
  return p;
}

const TF_SECONDS_FOR_LIMIT: Record<string, number> = {
  '1m': 60,
  '3m': 180,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '2h': 7200,
  '4h': 14400,
  '8h': 28800,
  '12h': 43200,
  '1d': 86400,
  '3d': 259200,
  '1w': 604800,
  '1M': 2592000,
};

function estimateCandleBarLimit(interval: HLCandleInterval, startTimeMs: number, endTimeMs: number): number {
  const sec = TF_SECONDS_FOR_LIMIT[interval] ?? 300;
  const span = Math.max(sec * 1000, endTimeMs - startTimeMs);
  const bars = Math.ceil(span / (sec * 1000));
  return Math.min(5000, Math.max(120, bars + 20));
}

function persistHlCandleCache(cacheKey: string, candles: HLCandlePoint[]): void {
  if (!candles.length) return;
  hlCandleCache.set(cacheKey, { ts: Date.now(), data: candles });
  try {
    sessionStorage.setItem(`ma_hl_candles_${cacheKey}`, JSON.stringify({ ts: Date.now(), data: candles }));
  } catch {}
}

function normalizeServerCandleRows(rows: any[]): HLCandlePoint[] {
  return rows
    .map((c: any) => {
      const rawTime = Number(c?.time);
      const time = Number.isFinite(rawTime) ? (rawTime < 1e12 ? rawTime * 1000 : rawTime) : NaN;
      const volRaw = Number(c?.volume ?? 0);
      return {
        time,
        open: Number(c?.open),
        high: Number(c?.high),
        low: Number(c?.low),
        close: Number(c?.close),
        volume: Number.isFinite(volRaw) && volRaw >= 0 ? volRaw : 0,
      };
    })
    .filter(
      (c: HLCandlePoint) =>
        Number.isFinite(c.time) &&
        Number.isFinite(c.open) &&
        Number.isFinite(c.high) &&
        Number.isFinite(c.low) &&
        Number.isFinite(c.close),
    )
    .sort((a, b) => a.time - b.time);
}

async function fetchServerTradeCandles(
  pair: string,
  interval: HLCandleInterval,
  limit: number,
): Promise<HLCandlePoint[]> {
  const pairLabel = pair.includes('-') || pair.includes('/') ? pair : `${pair}-USD`;
  const qs = new URLSearchParams({
    pair: pairLabel,
    timeframe: interval,
    limit: String(limit),
  });
  const parseResponse = (j: any): HLCandlePoint[] => {
    if (!j?.success) return [];
    const rows = Array.isArray(j.candles) ? j.candles : [];
    return normalizeServerCandleRows(rows);
  };

  try {
    const r = await miniappFetch(`/api/miniapp/builder/script-lab/candles?${qs.toString()}`, {
      headers: { 'X-Requested-With': 'Jester' },
    });
    const j = await r.json();
    const fromScriptLab = r.ok ? parseResponse(j) : [];
    if (fromScriptLab.length > 0) return fromScriptLab;
  } catch (err) {
    console.warn('[HL Direct] script-lab trade candles failed:', err);
  }

  try {
    const r2 = await miniappFetch(`/api/mini/chart-data?${qs.toString()}`, {
      headers: { 'X-Requested-With': 'Jester' },
    });
    const j2 = await r2.json();
    return r2.ok ? parseResponse(j2) : [];
  } catch (err) {
    console.warn('[HL Direct] mini chart-data trade candles failed:', err);
    return [];
  }
}

export type TradeChartCandleSource = 'direct' | 'server' | 'stale' | 'none';

/**
 * Trade chart candles: browser HL when allowed, else Jester server cache (one shared HL pool).
 * Avoids Telegram WebView /info 429 storms that blank the Trade page.
 */
export async function fetchTradeChartCandles(opts: {
  pair: string;
  interval: HLCandleInterval;
  startTimeMs: number;
  endTimeMs: number;
}): Promise<{ candles: HLCandlePoint[]; source: TradeChartCandleSource }> {
  const pairRaw = String(opts.pair || '').trim().toUpperCase();
  const interval = String(opts.interval || '').trim() as HLCandleInterval;
  const startTime = Number(opts.startTimeMs || 0);
  const endTime = Number(opts.endTimeMs || 0);
  if (!pairRaw || !interval || !Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) {
    return { candles: [], source: 'none' };
  }

  const parsed = parsePairForCandleSnapshot(pairRaw);
  const dex = String(parsed.dex || '').trim().toLowerCase();
  const cacheKey = `candles:${dex || 'core'}:${pairRaw}:${interval}:${startTime}:${endTime}:trade`;
  const stale = readStaleHlCandles(cacheKey);
  const barLimit = estimateCandleBarLimit(interval, startTime, endTime);

  if (!isHlInfoRateLimited()) {
    const direct = await fetchHLCandleSnapshot({
      pair: pairRaw,
      interval,
      startTimeMs: startTime,
      endTimeMs: endTime,
      limit: 5000,
    });
    if (direct.length > 0) {
      persistHlCandleCache(cacheKey, direct);
      return { candles: direct, source: 'direct' };
    }
    // Propr quotes core HL perps as BASE-USDC, which parses as HIP-3 xyz:BASE.
    // If that dex has no candles, use the core perp of the same coin.
    if (parsed.dex) {
      const core = await fetchHLCandleSnapshot({
        pair: parsed.coin,
        interval,
        startTimeMs: startTime,
        endTimeMs: endTime,
        limit: 5000,
      });
      if (core.length > 0) {
        return { candles: core, source: 'direct' };
      }
    }
  } else if (stale?.length) {
    return { candles: stale, source: 'stale' };
  }

  const server = await fetchServerTradeCandles(pairRaw, interval, barLimit);
  if (server.length > 0) {
    persistHlCandleCache(cacheKey, server);
    return { candles: server, source: 'server' };
  }
  if (parsed.dex) {
    const coreServer = await fetchServerTradeCandles(parsed.coin, interval, barLimit);
    if (coreServer.length > 0) {
      return { candles: coreServer, source: 'server' };
    }
  }

  if (stale?.length) return { candles: stale, source: 'stale' };
  return { candles: [], source: 'none' };
}

/**
 * Fetch current funding rate directly from Hyperliquid /info (browser-side).
 * Returns decimal funding (e.g. -0.0001) or null when unavailable.
 */
export async function fetchHLFundingRate(opts: { pair: string }): Promise<number | null> {
  const pairRaw = String(opts?.pair || '').trim();
  if (!pairRaw) return null;
  const parsed = parsePairForCandleSnapshot(pairRaw);
  const dex = String(parsed.dex || '').trim().toLowerCase();
  try {
    await resolveHlCoinName(parsed.coin, dex ? { dex } : undefined);
    const bundle = await fetchHLMetaAssetBundle(dex ? { dex } : undefined);
    const hit = bundle.funding.get(parsed.coin.toUpperCase());
    return hit != null ? hit : null;
  } catch {
    return null;
  }
}

/**
 * Full asset context (mark/oracle/24h vol/OI/funding/24h change) for a coin.
 * Used by the desktop terminal market header strip. Pulled from the same
 * `metaAndAssetCtxs` payload that powers fetchHLFundingRate so the network
 * cost is the same shape (cached briefly).
 */
export interface HLAssetCtx {
  coin: string;
  markPx: number | null;
  oraclePx: number | null;
  midPx: number | null;
  prevDayPx: number | null;
  dayVlmUsd: number | null;
  dayBaseVlm: number | null;
  openInterest: number | null;
  funding: number | null;
  premium: number | null;
}

const HL_CTX_TTL_MS = 12_000;
const hlCtxCache = new Map<string, { ts: number; map: Map<string, HLAssetCtx> }>();

function toFiniteNumber(v: unknown): number | null {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

export type HLMetaAssetBundle = {
  funding: Map<string, number>;
  ctx: Map<string, HLAssetCtx>;
  maxLeverage: Map<string, number>;
  szDecimals: Map<string, number>;
};

const HL_META_ASSET_TTL_MS = 12_000;
const hlMetaAssetBundleCache = new Map<string, { ts: number; bundle: HLMetaAssetBundle }>();
const hlMetaAssetBundleInFlight = new Map<string, Promise<HLMetaAssetBundle>>();

export async function fetchHLMetaAssetBundle(opts?: { dex?: string }): Promise<HLMetaAssetBundle> {
  const dex = String(opts?.dex || '').trim().toLowerCase();
  const cacheKey = dex || 'core';
  const now = Date.now();
  const cached = hlMetaAssetBundleCache.get(cacheKey);
  if (cached && now - cached.ts < HL_META_ASSET_TTL_MS) return cached.bundle;

  const inflight = hlMetaAssetBundleInFlight.get(cacheKey);
  if (inflight) return inflight;

  const p = (async (): Promise<HLMetaAssetBundle> => {
    try {
      const body = dex ? { type: 'metaAndAssetCtxs', dex } : { type: 'metaAndAssetCtxs' };
      const data = await hlInfoPost<any>(body);
      const meta = Array.isArray(data) ? data[0] : (data?.universe ? { universe: data.universe } : null);
      const ctxs = Array.isArray(data) ? data[1] : (data?.assetCtxs ?? data?.contexts);
      const universe = meta?.universe;
      const funding = new Map<string, number>();
      const ctx = new Map<string, HLAssetCtx>();
      const maxLeverage = new Map<string, number>();
      const szDecimals = new Map<string, number>();
      if (Array.isArray(universe) && Array.isArray(ctxs)) {
        for (let i = 0; i < Math.min(universe.length, ctxs.length); i++) {
          const u: any = universe[i] || {};
          const c: any = ctxs[i] || {};
          const name = String(u.name || '').trim();
          if (!name) continue;
          const lev = toFiniteNumber(u.maxLeverage);
          if (lev != null) {
            maxLeverage.set(name.toUpperCase(), lev);
            maxLeverage.set(name, lev);
          }
          const szDec = toFiniteNumber(u.szDecimals);
          if (szDec != null && szDec >= 0) {
            szDecimals.set(name.toUpperCase(), Math.floor(szDec));
            szDecimals.set(name, Math.floor(szDec));
          }
          const fundingVal = toFiniteNumber(c.funding);
          if (fundingVal != null) {
            funding.set(name.toUpperCase(), fundingVal);
            funding.set(name, fundingVal);
          }
          const markPx = toFiniteNumber(c.markPx);
          const midPx = toFiniteNumber(c.midPx);
          const oraclePx = toFiniteNumber(c.oraclePx);
          const prevDayPx = toFiniteNumber(c.prevDayPx);
          const dayBaseVlm = toFiniteNumber(c.dayBaseVlm);
          const dayNtlVlm = toFiniteNumber(c.dayNtlVlm);
          const openInterestBase = toFiniteNumber(c.openInterest);
          const premium = toFiniteNumber(c.premium);
          const dayVlmUsd = dayNtlVlm != null
            ? dayNtlVlm
            : (dayBaseVlm != null && markPx != null ? dayBaseVlm * markPx : null);
          const assetCtx: HLAssetCtx = {
            coin: name,
            markPx,
            oraclePx,
            midPx,
            prevDayPx,
            dayVlmUsd,
            dayBaseVlm,
            openInterest: openInterestBase != null && markPx != null ? openInterestBase * markPx : openInterestBase,
            funding: fundingVal,
            premium,
          };
          ctx.set(name.toUpperCase(), assetCtx);
          ctx.set(name, assetCtx);
        }
      }
      const bundle = { funding, ctx, maxLeverage, szDecimals };
      hlMetaAssetBundleCache.set(cacheKey, { ts: now, bundle });
      hlFundingCache.set(`funding:${cacheKey}`, { ts: now, map: funding });
      hlCtxCache.set(`ctx:${cacheKey}`, { ts: now, map: ctx });
      return bundle;
    } catch {
      const stale = hlMetaAssetBundleCache.get(cacheKey)?.bundle;
      if (stale) return stale;
      return { funding: new Map(), ctx: new Map(), maxLeverage: new Map(), szDecimals: new Map() };
    } finally {
      hlMetaAssetBundleInFlight.delete(cacheKey);
    }
  })();

  hlMetaAssetBundleInFlight.set(cacheKey, p);
  return p;
}

export async function fetchHLAssetCtx(opts: { pair: string }): Promise<HLAssetCtx | null> {
  const pairRaw = String(opts?.pair || '').trim();
  if (!pairRaw) return null;
  const parsed = parsePairForCandleSnapshot(pairRaw);
  const dex = String(parsed.dex || '').trim().toLowerCase();
  try {
    await resolveHlCoinName(parsed.coin, dex ? { dex } : undefined);
    const bundle = await fetchHLMetaAssetBundle(dex ? { dex } : undefined);
    return bundle.ctx.get(parsed.coin.toUpperCase()) ?? null;
  } catch {
    return null;
  }
}

/** Fetch meta + asset ctx bundles for core perps and all HIP-3 dexes (terminal pair picker). */
export async function fetchHLAllPerpMetaBundles(): Promise<Map<string, HLMetaAssetBundle>> {
  const dexKeys = ['core', ...HIP3_DEXS] as const;
  const results = await Promise.all(
    dexKeys.map(async (key) => {
      const bundle = await fetchHLMetaAssetBundle(key === 'core' ? undefined : { dex: key });
      return [key, bundle] as const;
    }),
  );
  return new Map(results);
}

export interface HLPerpMarketRowLookup {
  coin: string;
  dex: string;
  ctx: HLAssetCtx | null;
  maxLeverage: number | null;
  szDecimals: number | null;
}

/** Resolve live market stats for a pair symbol against pre-fetched perp bundles. */
export function lookupHLPerpMarketRow(
  pair: string,
  bundles: Map<string, HLMetaAssetBundle>,
): HLPerpMarketRowLookup {
  const parsed = parsePairForCandleSnapshot(pair);
  const dex = String(parsed.dex || '').trim().toLowerCase();
  const bundleKey = dex || 'core';
  const bundle = bundles.get(bundleKey) ?? bundles.get('core');
  const coin = parsed.coin.toUpperCase();
  return {
    coin,
    dex: bundleKey,
    ctx: bundle?.ctx.get(coin) ?? null,
    maxLeverage: bundle?.maxLeverage.get(coin) ?? null,
    szDecimals: bundle?.szDecimals?.get(coin) ?? null,
  };
}

export interface HLPosition {
  coin: string;
  szi: string;        // Size (signed, negative = short)
  entryPx: string;    // Entry price
  positionValue: string;
  unrealizedPnl: string;
  returnOnEquity: string;
  leverage: {
    type: string;
    value: number;
  };
  liquidationPx: string | null;
  marginUsed: string;
}

export interface HLClearinghouseState {
  assetPositions: Array<{
    position: HLPosition;
    type: string;
  }>;
  marginSummary: {
    accountValue: string;
    totalNtlPos: string;
    totalRawUsd: string;
    totalMarginUsed: string;
  };
  crossMarginSummary: {
    accountValue: string;
    totalNtlPos: string;
    totalRawUsd: string;
    totalMarginUsed: string;
  };
  withdrawable: string;
}

export interface ParsedPosition {
  pair: string;
  side: 'long' | 'short';
  size: number;
  notionalValue: number;
  entryPrice: number;
  currentPrice: number;
  pnl: number;
  pnlPercent: number;
  leverage: number;
  liquidationPrice: number | null;
  marginUsed: number;
}

export interface ParsedSpotBalance {
  coin: string;
  total: number;
  hold: number;
  available: number;
  usdValue: number;
  hasPrice: boolean;
}

export interface ParsedPortfolio {
  equity: number;
  withdrawable: number;
  marginUsed: number;
  /** USD collateral before unrealized PnL (HL marginSummary.totalRawUsd). */
  totalRawUsd?: number;
  /** Gross notional exposure across open positions (HL marginSummary.totalNtlPos). */
  totalNtlPos?: number;
  /** Spot wallet token balances (USDC, HYPE, etc.). */
  spotBalances?: ParsedSpotBalance[];
  positions: ParsedPosition[];
  totalUnrealizedPnl: number;
}

// ─────────────────────────────────────────────────────────────
// Portfolio caching / coalescing (prevents /info 429 spam)
// ─────────────────────────────────────────────────────────────

const HL_PORTFOLIO_TTL_MS = 30_000;
const portfolioCache = new Map<string, { ts: number; data: ParsedPortfolio }>();
const portfolioInFlight = new Map<string, Promise<ParsedPortfolio | null>>();

export type HLPortfolioPageBundle = {
  portfolio: ParsedPortfolio | null;
  fills90d: HLUserFill[];
  openOrders: any[];
};

const portfolioPageBundleInFlight = new Map<string, Promise<HLPortfolioPageBundle>>();
const HL_PORTFOLIO_PAGE_BUNDLE_TTL_MS = 22_000;
const portfolioPageBundleCache = new Map<string, { ts: number; data: HLPortfolioPageBundle }>();

/**
 * Coalesced portfolio-page fetch: one in-flight bundle per wallet so shell preload
 * and MiniAppPortfolio mount do not duplicate HL /info storms.
 */
export async function fetchHLPortfolioPageBundle(
  walletAddress: string,
  opts?: { forceFresh?: boolean; fillsDays?: number; includeFills?: boolean },
): Promise<HLPortfolioPageBundle> {
  const addr = String(walletAddress || '').trim().toLowerCase();
  if (!addr) return { portfolio: null, fills90d: [], openOrders: [] };
  const forceFresh = opts?.forceFresh === true;
  const includeFills = opts?.includeFills !== false;

  if (!forceFresh) {
    const cached = portfolioPageBundleCache.get(addr);
    if (cached && Date.now() - cached.ts < HL_PORTFOLIO_PAGE_BUNDLE_TTL_MS) {
      if (!includeFills || cached.data.fills90d.length > 0 || !cached.data.portfolio) {
        return {
          portfolio: cached.data.portfolio,
          fills90d: includeFills ? cached.data.fills90d : [],
          openOrders: cached.data.openOrders,
        };
      }
    }
    const existing = portfolioPageBundleInFlight.get(addr);
    if (existing) return existing;
  }

  const p = (async (): Promise<HLPortfolioPageBundle> => {
    const now = Date.now();
    const days = Math.max(1, opts?.fillsDays ?? 90);
    const start = now - days * 24 * 60 * 60 * 1000;
    const portfolio = await fetchHLPortfolio(addr, { forceFresh });
    const openOrders = await fetchHLFrontendOpenOrders(addr, { forceFresh });
    const fills90d = includeFills ? await fetchHLUserFillsByTime(addr, start, now) : [];
    const bundle = { portfolio, fills90d, openOrders };
    if (!forceFresh) {
      try {
        portfolioPageBundleCache.set(addr, { ts: Date.now(), data: bundle });
      } catch {
        /* ignore */
      }
    }
    return bundle;
  })().finally(() => {
    portfolioPageBundleInFlight.delete(addr);
  });

  portfolioPageBundleInFlight.set(addr, p);
  return p;
}

const HL_ABSTRACTION_MODE_TTL_MS = 10 * 60_000;
const abstractionModeCache = new Map<string, { ts: number; mode: string }>();

/** Fetch account abstraction mode (default, unifiedAccount, portfolioMargin, etc.). */
export async function fetchHLUserAbstraction(
  walletAddress: string,
  opts?: { forceFresh?: boolean }
): Promise<string> {
  const addr = String(walletAddress || '').trim().toLowerCase();
  if (!addr || !/^0x[a-f0-9]{40}$/.test(addr)) return 'default';
  const forceFresh = opts?.forceFresh === true;
  const cached = abstractionModeCache.get(addr);
  if (!forceFresh && cached && Date.now() - cached.ts < HL_ABSTRACTION_MODE_TTL_MS) return cached.mode;
  try {
    const raw = await hlInfoPost<unknown>({ type: 'userAbstraction', user: addr });
    const mode = typeof raw === 'string' ? raw : String(raw ?? 'default');
    abstractionModeCache.set(addr, { ts: Date.now(), mode });
    return mode;
  } catch {
    return 'default';
  }
}

/** Drop cached abstraction mode so UI can refresh after a signed mode change. */
export function invalidateHLUserAbstractionCache(walletAddress: string): void {
  const addr = String(walletAddress || '').trim().toLowerCase();
  if (!addr) return;
  abstractionModeCache.delete(addr);
  try {
    portfolioCache.delete(addr);
  } catch {}
}

export type HLAbstractionModeInfo = {
  raw: string;
  label: string;
  isPortfolioMargin: boolean;
  isUnifiedCollateral: boolean;
};

/** Human-readable label for Hyperliquid account abstraction modes. */
export function describeHLAbstractionMode(mode: string): HLAbstractionModeInfo {
  const raw = String(mode || 'default').trim() || 'default';
  const m = raw.toLowerCase();
  if (m === 'portfoliomargin' || m === 'portfolio_margin') {
    return {
      raw,
      label: 'Portfolio margin',
      isPortfolioMargin: true,
      isUnifiedCollateral: true,
    };
  }
  if (m === 'unifiedaccount' || m === 'unified_account') {
    return {
      raw,
      label: 'Unified account',
      isPortfolioMargin: false,
      isUnifiedCollateral: true,
    };
  }
  if (m === 'dexabstraction') {
    return { raw, label: 'DEX abstraction', isPortfolioMargin: false, isUnifiedCollateral: false };
  }
  if (m === 'disabled') {
    return { raw, label: 'Disabled', isPortfolioMargin: false, isUnifiedCollateral: false };
  }
  return { raw, label: 'Classic (default)', isPortfolioMargin: false, isUnifiedCollateral: false };
}

/** Returns true when collateral is in spot (unified account or portfolio margin). */
export function isHLUnifiedCollateralMode(mode: string): boolean {
  const m = String(mode || '').toLowerCase();
  return m === 'unifiedaccount' || m === 'portfoliomargin';
}

/** @deprecated Use isHLUnifiedCollateralMode */
function isUnifiedMode(mode: string): boolean {
  return isHLUnifiedCollateralMode(mode);
}

/** Fetch spot clearinghouse state (for unified/portfolio margin balance). */
export async function fetchHLSpotClearinghouseState(
  walletAddress: string
): Promise<{ balances: Array<{ coin: string; total: string; hold: string }> } | null> {
  const addr = String(walletAddress || '').trim().toLowerCase();
  if (!addr) return null;
  try {
    const data = await hlInfoPost<any>({ type: 'spotClearinghouseState', user: addr });
    const balances = Array.isArray((data as any)?.balances) ? (data as any).balances : [];
    return { balances };
  } catch {
    return null;
  }
}

/**
 * Fetch clearinghouse state directly from Hyperliquid.
 * This is a public endpoint - only requires wallet address.
 */
const HL_CH_STATE_TTL_MS = 8_000;
const hlChStateCache = new Map<string, { ts: number; data: HLClearinghouseState | null }>();
const hlChStateInFlight = new Map<string, Promise<HLClearinghouseState | null>>();

function hlChStateCacheKey(walletAddress: string, dex?: string): string {
  return `${String(walletAddress || '').trim().toLowerCase()}:${String(dex || 'core').trim().toLowerCase()}`;
}

export async function fetchHLClearinghouseState(
  walletAddress: string,
  opts?: { dex?: string; forceFresh?: boolean }
): Promise<HLClearinghouseState | null> {
  if (!walletAddress) return null;
  const dex = String(opts?.dex || '').trim().toLowerCase();
  const cacheKey = hlChStateCacheKey(walletAddress, dex);
  const cached = hlChStateCache.get(cacheKey);
  if (!opts?.forceFresh && cached && Date.now() - cached.ts < HL_CH_STATE_TTL_MS) return cached.data;

  const inflight = hlChStateInFlight.get(cacheKey);
  if (inflight) return inflight;

  const p = (async (): Promise<HLClearinghouseState | null> => {
    try {
      const data = await hlInfoPost<HLClearinghouseState>({
        type: 'clearinghouseState',
        user: walletAddress,
        ...(dex ? { dex } : {}),
      }, true, true);
      hlChStateCache.set(cacheKey, { ts: Date.now(), data: data as HLClearinghouseState });
      return data as HLClearinghouseState;
    } catch (err) {
      console.error('[HL Direct] Fetch error:', err);
      return cached?.data ?? null;
    } finally {
      hlChStateInFlight.delete(cacheKey);
    }
  })();

  hlChStateInFlight.set(cacheKey, p);
  return p;
}

const HL_MIDS_TTL_MS = 10_000;
const hlMidsCache = new Map<string, { ts: number; map: Map<string, number> }>();
const hlMidsInFlight = new Map<string, Promise<Map<string, number>>>();

/**
 * Fetch all asset mid prices from Hyperliquid.
 */
export async function fetchHLMidPrices(opts?: { dex?: string; forceFresh?: boolean }): Promise<Map<string, number>> {
  const dex = String(opts?.dex || '').trim().toLowerCase();
  const cacheKey = dex || 'core';
  const cached = hlMidsCache.get(cacheKey);
  if (!opts?.forceFresh && cached && Date.now() - cached.ts < HL_MIDS_TTL_MS) return cached.map;

  const inflight = hlMidsInFlight.get(cacheKey);
  if (inflight) return inflight;

  const p = (async (): Promise<Map<string, number>> => {
    const prices = new Map<string, number>();
    try {
      const data = await hlInfoPost<Record<string, string | number>>(
        dex ? { type: 'allMids', dex } : { type: 'allMids' }
      );
      ingestHlAllMids(prices, data, dex || undefined);
    } catch (err) {
      console.error('[HL Direct] Fetch mid prices error:', err);
      if (cached?.map) return cached.map;
    }

    hlMidsCache.set(cacheKey, { ts: Date.now(), map: prices });
    return prices;
  })().finally(() => {
    hlMidsInFlight.delete(cacheKey);
  });

  hlMidsInFlight.set(cacheKey, p);
  return p;
}

/** Parse spot wallet balances into token rows with approximate USD value. */
export function parseSpotBalances(
  balances: Array<{ coin: string; total: string; hold: string }>,
  mids: Map<string, number>,
  hip3Mids: Map<string, number>[] = [],
): ParsedSpotBalance[] {
  const stable = new Set(['USDC', 'USDT', 'USD']);
  const priceForCoin = (coin: string): { px: number; hasPrice: boolean } => {
    const upper = String(coin || '').toUpperCase().trim();
    if (!upper) return { px: 0, hasPrice: false };
    if (stable.has(upper)) return { px: 1, hasPrice: true };
    const core = mids.get(upper);
    if (Number.isFinite(core) && (core as number) > 0) return { px: core as number, hasPrice: true };
    for (const m of hip3Mids) {
      for (const [pair, px] of m) {
        const base = String(pair || '').split('-')[0]?.toUpperCase();
        if (base === upper && Number.isFinite(px) && px > 0) return { px, hasPrice: true };
      }
    }
    return { px: 0, hasPrice: false };
  };

  const rows: ParsedSpotBalance[] = [];
  for (const b of balances || []) {
    const coin = String(b.coin ?? '').trim();
    if (!coin) continue;
    const total = parseFloat(b.total ?? '0') || 0;
    if (total <= 0) continue;
    const hold = parseFloat(b.hold ?? '0') || 0;
    const available = Math.max(0, total - hold);
    const { px, hasPrice } = priceForCoin(coin);
    const usdValue = hasPrice ? total * px : 0;
    rows.push({
      coin: coin.toUpperCase(),
      total,
      hold,
      available,
      usdValue: Math.round(usdValue * 100) / 100,
      hasPrice,
    });
  }
  return rows.sort((a, b) => b.usdValue - a.usdValue || b.total - a.total);
}

function mergeSpotBalances(
  a: ParsedSpotBalance[] = [],
  b: ParsedSpotBalance[] = [],
): ParsedSpotBalance[] {
  const map = new Map<string, ParsedSpotBalance>();
  for (const item of [...a, ...b]) {
    const key = item.coin.toUpperCase();
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { ...item });
      continue;
    }
    map.set(key, {
      coin: key,
      total: existing.total + item.total,
      hold: existing.hold + item.hold,
      available: existing.available + item.available,
      usdValue: Math.round((existing.usdValue + item.usdValue) * 100) / 100,
      hasPrice: existing.hasPrice && item.hasPrice,
    });
  }
  return Array.from(map.values())
    .filter((x) => x.total > 0)
    .sort((x, y) => y.usdValue - x.usdValue || y.total - x.total);
}

export function spotBalanceTotals(spotBalances: ParsedSpotBalance[]): { equity: number; withdrawable: number } {
  let equity = 0;
  let withdrawable = 0;
  for (const b of spotBalances) {
    // Never treat raw token units as USD (e.g. dust airdrops like MAX with no spot mid).
    equity += b.hasPrice ? b.usdValue : 0;
    if (b.coin === 'USDC') withdrawable = b.available;
  }
  if (withdrawable === 0) {
    withdrawable = spotBalances.reduce((sum, b) => sum + b.available, 0);
  }
  return {
    equity: Math.round(equity * 100) / 100,
    withdrawable: Math.round((withdrawable || equity) * 100) / 100,
  };
}

/**
 * Parse Hyperliquid clearinghouse state into a cleaner format.
 */
export function parseClearinghouseState(
  state: HLClearinghouseState,
  midPrices?: Map<string, number>,
  opts?: { dex?: string }
): ParsedPortfolio {
  const positions: ParsedPosition[] = [];
  let totalUnrealizedPnl = 0;
  const dex = String(opts?.dex || '').trim().toLowerCase();
  const collateralForDex = (d: string): string => {
    if (!d) return 'USD';
    if (d === 'xyz') return 'USDC';
    if (d === 'cash') return 'USDT';
    return 'USDC';
  };

  for (const ap of state.assetPositions || []) {
    const pos = ap.position;
    if (!pos) continue;

    const size = parseFloat(pos.szi) || 0;
    if (size === 0) continue; // Skip zero positions

    const entryPrice = parseFloat(pos.entryPx) || 0;
    const unrealizedPnl = parseFloat(pos.unrealizedPnl) || 0;
    const positionValue = parseFloat(pos.positionValue) || 0;
    const marginUsed = parseFloat(pos.marginUsed) || 0;
    const liquidationPx = pos.liquidationPx ? parseFloat(pos.liquidationPx) : null;
    
    const coinUpper = String(pos.coin || '').toUpperCase().trim();
    const coinNoDex = coinUpper.includes(':') ? coinUpper.split(':', 2)[1] || coinUpper : coinUpper;
    const pairKey = dex
      ? (coinNoDex.includes('-') ? coinNoDex : `${coinNoDex}-${collateralForDex(dex)}`)
      : coinNoDex;

    const midPx = midPrices ? lookupPositionMark(midPrices, pairKey) : NaN;
    const currentPrice = midPx > 0 ? midPx : (entryPrice > 0 ? entryPrice : 0);
    
    // Calculate PnL percent based on margin used (ROE)
    const roe = parseFloat(pos.returnOnEquity) || 0;
    const pnlPercent = roe * 100;

    const leverage = pos.leverage?.value || 1;

    positions.push({
      pair: pairKey,
      side: size > 0 ? 'long' : 'short',
      size: Math.abs(size),
      notionalValue: Math.abs(positionValue),
      entryPrice,
      currentPrice,
      pnl: unrealizedPnl,
      pnlPercent,
      leverage,
      liquidationPrice: liquidationPx,
      marginUsed,
    });

    totalUnrealizedPnl += unrealizedPnl;
  }

  // Full account equity (marginSummary includes isolated positions; crossMarginSummary does not).
  const summary = state.marginSummary || state.crossMarginSummary;
  const equity = parseFloat(summary?.accountValue) || 0;
  const withdrawable = parseFloat(state.withdrawable) || 0;
  const marginUsed = parseFloat(summary?.totalMarginUsed) || 0;
  const totalRawUsd = parseFloat(summary?.totalRawUsd) || 0;
  const totalNtlPos = parseFloat(summary?.totalNtlPos) || 0;

  return {
    equity,
    withdrawable,
    marginUsed,
    totalRawUsd,
    totalNtlPos,
    positions,
    totalUnrealizedPnl,
  };
}

/**
 * Convenience function: fetch and parse portfolio in one call.
 */
export async function fetchHLPortfolio(
  walletAddress: string,
  opts?: { forceFresh?: boolean }
): Promise<ParsedPortfolio | null> {
  const addr = String(walletAddress || '').trim().toLowerCase();
  if (!addr) return null;
  const forceFresh = opts?.forceFresh === true;

  if (!forceFresh) {
    try {
      const cached = portfolioCache.get(addr);
      if (cached && Date.now() - cached.ts < HL_PORTFOLIO_TTL_MS) return cached.data;
    } catch {}
  }

  const inFlight = portfolioInFlight.get(addr);
  if (inFlight) return inFlight;

  const p = (async (): Promise<ParsedPortfolio | null> => {
    try {
      const readSpotBalancePortfolio = (): Pick<
        ParsedPortfolio,
        'equity' | 'withdrawable' | 'spotBalances'
      > | null => {
        if (!spotState?.balances?.length) return null;
        const spotBalances = parseSpotBalances(spotState.balances, coreMids, hip3Mids);
        if (!spotBalances.length) return null;
        const totals = spotBalanceTotals(spotBalances);
        return totals.equity > 0 || totals.withdrawable > 0
          ? { ...totals, spotBalances }
          : null;
      };

      // Mode detection: unified/portfolio margin users have balance in spotClearinghouseState, not clearinghouseState
      const modeP = fetchHLUserAbstraction(addr);
      const coreStateP = fetchHLClearinghouseState(addr, { forceFresh });
      const coreMidsP = fetchHLMidPrices({ forceFresh });
      const spotStateP = fetchHLSpotClearinghouseState(addr);
      const [mode, coreState, coreMids, spotState] = await Promise.all([modeP, coreStateP, coreMidsP, spotStateP]);

      const hip3States: (HLClearinghouseState | null)[] = [];
      const hip3Mids: Map<string, number>[] = [];
      for (const dex of HIP3_DEXS) {
        const [state, mids] = await Promise.all([
          fetchHLClearinghouseState(addr, { dex, forceFresh }),
          fetchHLMidPrices({ dex, forceFresh }),
        ]);
        hip3States.push(state);
        hip3Mids.push(mids);
      }

      const mergeTwo = (a: ParsedPortfolio | null, b: ParsedPortfolio | null): ParsedPortfolio | null => {
        if (!a) return b;
        if (!b) return a;
        return {
          equity: (a.equity || 0) + (b.equity || 0),
          withdrawable: (a.withdrawable || 0) + (b.withdrawable || 0),
          marginUsed: (a.marginUsed || 0) + (b.marginUsed || 0),
          totalRawUsd: (a.totalRawUsd || 0) + (b.totalRawUsd || 0),
          totalNtlPos: (a.totalNtlPos || 0) + (b.totalNtlPos || 0),
          spotBalances: mergeSpotBalances(a.spotBalances, b.spotBalances),
          positions: [...(a.positions || []), ...(b.positions || [])],
          totalUnrealizedPnl: (a.totalUnrealizedPnl || 0) + (b.totalUnrealizedPnl || 0),
        };
      };

      // Dedupe positions: same pair+side can appear from core + HIP-3. Keep larger size.
      const dedupePositions = (positions: ParsedPosition[]): ParsedPosition[] => {
        const seen = new Map<string, ParsedPosition>();
        for (const p of positions || []) {
          const key = `${String(p.pair || '').toUpperCase()}_${p.side}`;
          const existing = seen.get(key);
          if (!existing || (existing.size || 0) < (p.size || 0)) seen.set(key, p);
        }
        return Array.from(seen.values());
      };

      const mergeAll = (portfolios: (ParsedPortfolio | null)[]): ParsedPortfolio | null => {
        const merged = portfolios.reduce<ParsedPortfolio | null>((acc, p) => mergeTwo(acc, p), null);
        if (!merged?.positions?.length) return merged;
        return { ...merged, positions: dedupePositions(merged.positions) };
      };

      let merged: ParsedPortfolio | null = null;

      if (isUnifiedMode(mode)) {
        // Unified/portfolio margin: balance from spotClearinghouseState; positions from clearinghouse
        const spotBalance = readSpotBalancePortfolio();
        const equity = spotBalance?.equity || 0;
        const withdrawable = spotBalance?.withdrawable || 0;
        const core = coreState ? parseClearinghouseState(coreState, coreMids) : null;
        const hip3Portfolios = HIP3_DEXS.map((dex, i) =>
          hip3States[i] ? parseClearinghouseState(hip3States[i]!, hip3Mids[i], { dex }) : null
        );
        merged = mergeAll([core, ...hip3Portfolios].filter(Boolean) as ParsedPortfolio[]);
        if (merged) {
          merged = {
            ...merged,
            equity: equity || merged.equity,
            withdrawable: withdrawable || merged.withdrawable || equity,
            spotBalances: spotBalance?.spotBalances ?? merged.spotBalances,
          };
        } else if (equity > 0 || withdrawable > 0) {
          merged = {
            equity,
            withdrawable: withdrawable || equity,
            marginUsed: 0,
            positions: [],
            totalUnrealizedPnl: 0,
            spotBalances: spotBalance?.spotBalances,
          };
        }
      } else {
        // Default/standard: balance from clearinghouseState (core + all HIP-3 DEXs)
        const hasAnyState = coreState || hip3States.some(Boolean);
        if (!hasAnyState) return null;
        const core = coreState ? parseClearinghouseState(coreState, coreMids) : null;
        const hip3Portfolios = HIP3_DEXS.map((dex, i) =>
          hip3States[i] ? parseClearinghouseState(hip3States[i]!, hip3Mids[i], { dex }) : null
        );
        merged = mergeAll([core, ...hip3Portfolios].filter(Boolean) as ParsedPortfolio[]);

        // If `userAbstraction` was rate-limited, unified/portfolio-margin accounts look like
        // default accounts and clearinghouseState can report 0 accountValue. Probe spot balance
        // before surfacing a false zero-equity portfolio.
        if (merged && (merged.equity || 0) <= 0 && (merged.positions || []).length === 0) {
          const spotBalance = readSpotBalancePortfolio();
          if (spotBalance) {
            merged = {
              ...merged,
              equity: spotBalance.equity,
              withdrawable: spotBalance.withdrawable,
              spotBalances: spotBalance.spotBalances,
            };
          }
        }
      }

      if (!merged) return null;

      const markMap = new Map<string, number>();
      for (const [k, v] of coreMids || []) markMap.set(k, v);
      for (const m of hip3Mids) {
        for (const [k, v] of m) markMap.set(k, v);
      }
      const marked = applyLiveMarksToPortfolio(merged, markMap);
      if (marked) merged = marked;

      if (spotState?.balances?.length) {
        const spotBalances = parseSpotBalances(spotState.balances, coreMids, hip3Mids);
        if (spotBalances.length) {
          merged = { ...merged, spotBalances };
        }
      }

      if (merged && !forceFresh) {
        try {
          portfolioCache.set(addr, { ts: Date.now(), data: merged });
        } catch {}
      }
      return merged;
    } catch (err) {
      console.warn('[HL Direct] portfolio fetch error:', err);
      return null;
    } finally {
      portfolioInFlight.delete(addr);
    }
  })();

  portfolioInFlight.set(addr, p);
  return p;
}

// ─────────────────────────────────────────────────────────────
// Portfolio performance (HL `portfolio` info endpoint)
// Account-level equity/PnL curves per period bucket — the same data
// account trackers (CMM Hypertracker, HL web UI) build their ROI from.
// ─────────────────────────────────────────────────────────────

/** One period bucket from the HL `portfolio` endpoint (day/week/month/allTime + perp* variants). */
export interface HLPortfolioPeriodData {
  accountValueHistory: Array<[number, string]>;
  pnlHistory: Array<[number, string]>;
  vlm: string;
}

export type HLPortfolioPerformance = Array<[string, HLPortfolioPeriodData]>;

const HL_PORTFOLIO_PERF_TTL_MS = 5 * 60_000;
const portfolioPerfCache = new Map<string, { ts: number; data: HLPortfolioPerformance }>();
const portfolioPerfInFlight = new Map<string, Promise<HLPortfolioPerformance>>();

export async function fetchHLPortfolioPerformance(walletAddress: string): Promise<HLPortfolioPerformance> {
  const addr = String(walletAddress || '').trim().toLowerCase();
  if (!addr) return [];

  const cached = portfolioPerfCache.get(addr);
  if (cached && Date.now() - cached.ts < HL_PORTFOLIO_PERF_TTL_MS) return cached.data;

  const inFlight = portfolioPerfInFlight.get(addr);
  if (inFlight) return inFlight;

  const p = (async (): Promise<HLPortfolioPerformance> => {
    try {
      const data = await hlInfoPost<unknown>({ type: 'portfolio', user: addr });
      const periods: HLPortfolioPerformance = Array.isArray(data)
        ? (data as any[]).filter(
            (entry): entry is [string, HLPortfolioPeriodData] =>
              Array.isArray(entry) && typeof entry[0] === 'string' && entry[1] && typeof entry[1] === 'object'
          )
        : [];
      portfolioPerfCache.set(addr, { ts: Date.now(), data: periods });
      return periods;
    } catch (err) {
      console.warn('[HL Direct] portfolio performance fetch error:', err);
      return [];
    } finally {
      portfolioPerfInFlight.delete(addr);
    }
  })();

  portfolioPerfInFlight.set(addr, p);
  return p;
}

export async function computePnlSummaryWithAccountCurve(opts: {
  walletAddress: string;
  fills: HLUserFill[];
  equityNow: number;
  nowMs?: number;
}): Promise<ParsedPnlSummary> {
  let hlPortfolioPeriods: HLPortfolioPerformance | null = null;
  try {
    hlPortfolioPeriods = await fetchHLPortfolioPerformance(opts.walletAddress);
  } catch {
    hlPortfolioPeriods = null;
  }
  return computePnlSummaryFromFills({
    fills: opts.fills,
    equityNow: opts.equityNow,
    nowMs: opts.nowMs,
    hlPortfolioPeriods,
  });
}

/**
 * Account ROI over a rolling N-day window, sliced from the smallest HL period bucket
 * that covers it (week → month → allTime). Equity-curve based, so deposits/withdrawals
 * move it — same caveat as any account tracker.
 */
export function computeHLAccountRoiForWindow(
  periods: HLPortfolioPerformance,
  days: number,
  nowMs = Date.now(),
): { roiPct: number; deltaUsd: number } | null {
  if (!Array.isArray(periods) || periods.length === 0) return null;
  const bucketOrder = days <= 1 ? ['day', 'week', 'month', 'allTime']
    : days <= 7 ? ['week', 'month', 'allTime']
    : ['month', 'allTime'];
  const startMs = nowMs - days * 24 * 60 * 60 * 1000;

  for (const bucket of bucketOrder) {
    const entry = periods.find(([key]) => key === bucket);
    const hist = entry?.[1]?.accountValueHistory;
    if (!Array.isArray(hist) || hist.length < 2) continue;

    // History is chronological and always extends to ~now; the first sample at/after
    // startMs marks the window start (index 0 when the account is younger than the window).
    let startIdx = hist.findIndex(([t]) => Number(t) >= startMs);
    if (startIdx < 0) startIdx = 0;

    const startVal = Number(hist[startIdx]![1]);
    const endVal = Number(hist[hist.length - 1]![1]);
    if (!Number.isFinite(startVal) || !Number.isFinite(endVal)) continue;
    const deltaUsd = endVal - startVal;
    const roiPct = startVal > 0 ? (deltaUsd / startVal) * 100 : 0;
    return {
      roiPct: Math.round(roiPct * 100) / 100,
      deltaUsd: Math.round(deltaUsd * 100) / 100,
    };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// WebSocket for live updates
// ─────────────────────────────────────────────────────────────

type HLWebSocketCallback = (portfolio: ParsedPortfolio) => void;
type HLOrderUpdateCallback = (orders: any[]) => void;

let wsInstance: WebSocket | null = null;
let wsWalletAddress: string | null = null;
let wsCallbacks: Set<HLWebSocketCallback> = new Set();
let wsOrderCallbacks: Set<HLOrderUpdateCallback> = new Set();
let wsReconnectTimeout: NodeJS.Timeout | null = null;
let wsPendingPortfolioTimer: NodeJS.Timeout | null = null;
let wsPingTimer: ReturnType<typeof setInterval> | null = null;
let wsMidsEmitTimer: ReturnType<typeof setTimeout> | null = null;
let wsLastPortfolioFetchTs = 0;
let wsLastPortfolio: ParsedPortfolio | null = null;
const wsLiveMids = new Map<string, number>();
const WS_PORTFOLIO_MIN_INTERVAL_MS = 1200;
const WS_MIDS_EMIT_MS = 200;
const WS_PING_MS = 30_000;
const WS_ALL_MIDS_DEXS = ['xyz', 'cash', 'km', 'para'] as const;

function clearHlWsTimers(): void {
  if (wsPendingPortfolioTimer) {
    clearTimeout(wsPendingPortfolioTimer);
    wsPendingPortfolioTimer = null;
  }
  if (wsPingTimer) {
    clearInterval(wsPingTimer);
    wsPingTimer = null;
  }
  if (wsMidsEmitTimer) {
    clearTimeout(wsMidsEmitTimer);
    wsMidsEmitTimer = null;
  }
}

function publishHlWsPortfolio(portfolio: ParsedPortfolio, force = false): void {
  const marked = applyLiveMarksToPortfolio(portfolio, wsLiveMids);
  const next = marked || portfolio;
  wsLastPortfolio = next;
  const emit = () => {
    const snap = wsLastPortfolio;
    if (!snap) return;
    wsCallbacks.forEach((cb) => {
      try {
        cb(snap);
      } catch {}
    });
  };
  if (force) {
    if (wsMidsEmitTimer) {
      clearTimeout(wsMidsEmitTimer);
      wsMidsEmitTimer = null;
    }
    emit();
    return;
  }
  if (wsMidsEmitTimer) return;
  wsMidsEmitTimer = setTimeout(() => {
    wsMidsEmitTimer = null;
    emit();
  }, WS_MIDS_EMIT_MS);
}

function applyWsAllMids(msg: { data?: unknown }): void {
  ingestHlAllMids(wsLiveMids, msg?.data);
  if (!wsLastPortfolio || wsCallbacks.size === 0) return;
  const marked = applyLiveMarksToPortfolio(wsLastPortfolio, wsLiveMids);
  if (!marked) return;
  publishHlWsPortfolio(marked);
}

// ─────────────────────────────────────────────────────────────
// Open orders (direct /info + WS orderUpdates)
// ─────────────────────────────────────────────────────────────

const HL_OPEN_ORDERS_TTL_MS = 5000;
const openOrdersCache = new Map<string, { ts: number; data: any[] }>();
const openOrdersInFlight = new Map<string, Promise<any[]>>();

/** WS `orderUpdates` can burst; each refresh hits 3× `/info` (dexes). Throttle coalesces bursts. */
const HL_WS_OPEN_ORDERS_DEBOUNCE_MS = 450;
const HL_WS_OPEN_ORDERS_MIN_GAP_MS = 2800;
let hlWsOpenOrdersTimer: ReturnType<typeof setTimeout> | null = null;
let hlWsOpenOrdersCooldownTimer: ReturnType<typeof setTimeout> | null = null;
let hlWsOpenOrdersLastRunMs = 0;

function normalizeHlPairNameLikeServer(pair: string): string {
  if (!pair) return pair;
  const upper = String(pair).toUpperCase();
  const stripped = upper
    .replace(/USDT/g, '')
    .replace(/-USD/g, '')
    .replace(/\/USD/g, '')
    .replace(/-PERP/g, '')
    .replace(/PERP/g, '');
  return stripped.replace(/[^A-Z]/g, '');
}

function inferTpslType(order: any): 'sl' | 'tp' | null {
  // HL API may return trigger under `trigger` or `t.trigger` (abbreviated)
  const nested =
    (order as any)?.t?.trigger?.tpsl ??
    (order as any)?.trigger?.tpsl;
  if (nested === 'sl' || nested === 'tp') return nested;
  const top = String((order as any)?.tpsl || '').toLowerCase().trim();
  if (top === 'sl' || top === 'tp') return top;
  // frontendOpenOrders uses these exact strings (match server HyperliquidClient.findPositionOrders)
  const otRaw = String((order as any)?.orderType || '');
  const ot = otRaw.trim();
  const up = ot.toUpperCase();
  if (up === 'SL' || up === 'TP') return up === 'SL' ? 'sl' : 'tp';
  if (ot === 'Stop Limit' || ot === 'Stop Market') return 'sl';
  if (ot === 'Take Profit Limit' || ot === 'Take Profit Market') return 'tp';
  const otl = otRaw.toLowerCase();
  if (otl.includes('stop')) return 'sl';
  if (otl.includes('take profit') || (otl.includes('take') && otl.includes('profit'))) return 'tp';
  if (otl.includes('profit') && (otl.includes('tp') || /\btp\b/.test(otl))) return 'tp';
  const tc = String((order as any)?.triggerCondition || '').toLowerCase();
  if (tc.includes('sl')) return 'sl';
  if (tc.includes('tp')) return 'tp';
  return null;
}

/** Parse trigger / limit price from HL open-order shapes (often strings). */
function parseHlOrderPx(order: any): number {
  const raw =
    (order as any)?.t?.trigger?.triggerPx ??
    (order as any)?.trigger?.triggerPx ??
    (order as any)?.t?.trigger?.px ??
    (order as any)?.trigger?.px ??
    (order as any)?.triggerPx ??
    (order as any)?.limitPx ??
    (order as any)?.price ??
    (order as any)?.px;
  if (raw === undefined || raw === null || raw === '') return NaN;
  const n = parseFloat(String(raw).replace(/,/g, ''));
  return Number.isFinite(n) && n > 0 ? n : NaN;
}

/** When HL omits `tpsl` / friendly orderType, infer from trigger vs entry (reduce-only protection orders). */
function inferTpslFromPriceVsEntry(px: number, entry: number, side: 'long' | 'short'): 'sl' | 'tp' | null {
  if (!Number.isFinite(px) || !Number.isFinite(entry) || entry <= 0 || px <= 0) return null;
  const eps = Math.max(entry * 1e-9, 1e-12);
  if (side === 'long') {
    if (px < entry - eps) return 'sl';
    if (px > entry + eps) return 'tp';
  } else {
    if (px > entry + eps) return 'sl';
    if (px < entry - eps) return 'tp';
  }
  return null;
}

function parseHlOrderSide(order: any): 'buy' | 'sell' | null {
  const boolSide = order?.b ?? order?.isBuy;
  if (typeof boolSide === 'boolean') return boolSide ? 'buy' : 'sell';
  const raw = String(
    order?.side ??
    order?.dir ??
    order?.direction ??
    order?.orderSide ??
    order?.b ??
    '',
  ).trim().toLowerCase();
  if (!raw) return null;
  if (raw === 'b' || raw === 'buy' || raw === 'bid' || raw === 'long') return 'buy';
  if (raw === 'a' || raw === 'ask' || raw === 'sell' || raw === 'short') return 'sell';
  if (raw.includes('buy') || raw.includes('bid')) return 'buy';
  if (raw.includes('sell') || raw.includes('ask')) return 'sell';
  return null;
}

function orderClosesPositionSide(order: any, side: 'long' | 'short'): boolean {
  const orderSide = parseHlOrderSide(order);
  if (orderSide == null) return true;
  return side === 'long' ? orderSide === 'sell' : orderSide === 'buy';
}

export type ComputeTpslContext = { entryPrice: number; side: 'long' | 'short' };

export async function fetchHLFrontendOpenOrders(walletAddress: string, opts?: { forceFresh?: boolean }): Promise<any[]> {
  const addr = String(walletAddress || '').trim().toLowerCase();
  if (!addr) return [];

  const now = Date.now();
  const cached = openOrdersCache.get(addr);
  if (!opts?.forceFresh && cached && (now - cached.ts) < HL_OPEN_ORDERS_TTL_MS) return cached.data;

  const inFlight = openOrdersInFlight.get(addr);
  if (inFlight) return inFlight;

  const p = (async () => {
    try {
      // Fetch default (main) perp + HIP-3 dexes (xyz, cash) so SL/TP orders for HIP-3 pairs are included
      const dexes = ['', 'xyz', 'cash'] as const;
      const results = await Promise.all(
        dexes.map(async (dex) => {
          try {
            return (await hlInfoPost<any[]>({ type: 'frontendOpenOrders', user: addr, ...(dex ? { dex } : {}) }, true, true)) ?? [];
          } catch {
            return [];
          }
        })
      );
      const seen = new Set<string>();
      const list: any[] = [];
      for (const data of results) {
        const arr = Array.isArray(data) ? data : [];
        for (const o of arr) {
          const key = `${o?.coin ?? ''}:${o?.oid ?? ''}:${o?.limitPx ?? ''}`;
          if (!seen.has(key)) {
            seen.add(key);
            list.push(o);
          }
        }
      }
      openOrdersCache.set(addr, { ts: Date.now(), data: list });
      return list;
    } catch {
      return [];
    } finally {
      openOrdersInFlight.delete(addr);
    }
  })();

  openOrdersInFlight.set(addr, p);
  return p;
}

export function computeHasTpslForPair(
  orders: any[],
  pair: string,
  ctx?: ComputeTpslContext
): { hasSl: boolean; hasTp: boolean } {
  const r = computeTpslPricesForPair(orders, pair, ctx);
  return { hasSl: r.slPrice != null, hasTp: r.tpPrice != null };
}

export type HlOpenOrderChartLine = {
  price: number;
  label: string;
  kind: 'sl' | 'tp' | 'limit' | 'order';
};

/**
 * All resting HL open-order prices for a pair (not just the first SL/TP).
 * Used to overlay multiple TP/SL/limit lines on terminal charts.
 */
export function listOpenOrderChartLinesForPair(
  orders: any[],
  pair: string,
  ctx?: ComputeTpslContext & { mid?: number },
): HlOpenOrderChartLine[] {
  const p = String(pair || '').toUpperCase().trim();
  const hlPair = normalizeHlPairNameLikeServer(p);
  const base = p.includes('-') ? p.split('-')[0] : (p.includes('/') ? p.split('/')[0] : p);
  const candidates = new Set([p, hlPair, base].filter(Boolean));

  const hip3Quotes = ['USDC', 'USDH', 'USDE', 'USDT0', 'USDT'];
  const parts = p.split('-', 2);
  const quote = (parts[1] || '').trim();
  const isHip3 = p.includes('-') && quote !== 'USD' && hip3Quotes.includes(quote);
  if (isHip3 && base) {
    const dex = (quote === 'USDT' || quote === 'USDT0') ? 'cash' : 'xyz';
    candidates.add(`${dex.toUpperCase()}:${base}`);
    candidates.add(`${dex}:${base}`);
  }

  const lines: HlOpenOrderChartLine[] = [];
  const seen = new Set<string>();

  for (const order of Array.isArray(orders) ? orders : []) {
    if (!order) continue;
    const orderCoin = String((order as any).coin ?? (order as any).asset ?? (order as any).symbol ?? '').toUpperCase();
    const orderBase = orderCoin.includes(':') ? orderCoin.split(':').pop() || orderCoin : orderCoin;
    const matches =
      candidates.has(orderCoin) ||
      candidates.has(orderBase) ||
      (base && orderBase === base);
    if (!matches) continue;

    const px = parseHlOrderPx(order);
    if (!Number.isFinite(px) || px <= 0) continue;

    const orderTypeStr = String((order as any)?.orderType || '');
    const isTrigger =
      (order as any).isTrigger === true ||
      Boolean((order as any)?.trigger) ||
      Boolean((order as any)?.t?.trigger) ||
      /\b(stop|take\s+profit|trigger)\b/i.test(orderTypeStr);

    let kind: HlOpenOrderChartLine['kind'] = 'order';
    const fromFields = inferTpslType(order);
    if (fromFields === 'sl' || fromFields === 'tp') {
      kind = fromFields;
    } else if (isTrigger && ctx?.entryPrice && (ctx.side === 'long' || ctx.side === 'short')) {
      const inferred = inferTpslFromPriceVsEntry(px, Number(ctx.entryPrice), ctx.side);
      if (inferred) kind = inferred;
    } else if (isTrigger && ctx?.mid && Number(ctx.mid) > 0) {
      // No position: classify trigger vs mid (long-bias: below = SL, above = TP).
      kind = px < Number(ctx.mid) ? 'sl' : 'tp';
    } else if (!isTrigger) {
      kind = 'limit';
    }

    const key = `${kind}:${px.toFixed(6)}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const label =
      kind === 'sl' ? 'SL' : kind === 'tp' ? 'TP' : kind === 'limit' ? 'Limit' : 'Order';
    lines.push({ price: px, label, kind });
  }

  lines.sort((a, b) => b.price - a.price);
  // Disambiguate duplicates visually: TP, TP2… / SL, SL2…
  const counts: Record<string, number> = {};
  return lines.map((ln) => {
    const n = (counts[ln.kind] = (counts[ln.kind] || 0) + 1);
    if (n === 1) return ln;
    return { ...ln, label: `${ln.label}${n}` };
  });
}

/** Extract SL and TP trigger prices from open orders for a pair. */
export function computeTpslPricesForPair(
  orders: any[],
  pair: string,
  ctx?: ComputeTpslContext
): { slPrice: number | null; tpPrice: number | null } {
  const p = String(pair || '').toUpperCase().trim();
  const hlPair = normalizeHlPairNameLikeServer(p);
  const base = p.includes('-') ? p.split('-')[0] : (p.includes('/') ? p.split('/')[0] : p);
  const candidates = new Set([p, hlPair, base].filter(Boolean));

  // HIP-3: Hyperliquid returns order.coin as "xyz:SILVER" or "XYZ:SILVER" for SILVER-USDC
  const hip3Quotes = ['USDC', 'USDH', 'USDE', 'USDT0', 'USDT'];
  const parts = p.split('-', 2);
  const quote = (parts[1] || '').trim();
  const isHip3 = p.includes('-') && quote !== 'USD' && hip3Quotes.includes(quote);
  if (isHip3 && base) {
    const dex = (quote === 'USDT' || quote === 'USDT0') ? 'cash' : 'xyz';
    candidates.add(`${dex.toUpperCase()}:${base}`);
    candidates.add(`${dex}:${base}`);
  }

  let slPrice: number | null = null;
  let tpPrice: number | null = null;

  for (const order of Array.isArray(orders) ? orders : []) {
    if (!order) continue;
    const orderCoin = String((order as any).coin ?? (order as any).asset ?? (order as any).symbol ?? '').toUpperCase();
    const orderBase = orderCoin.includes(':') ? orderCoin.split(':').pop() || orderCoin : orderCoin;
    const matches =
      candidates.has(orderCoin) ||
      candidates.has(orderBase) ||
      (base && orderBase === base);
    if (!matches) continue;
    const orderTypeStr = String((order as any)?.orderType || '');
    const isTrigger =
      (order as any).isTrigger === true ||
      Boolean((order as any)?.trigger) ||
      Boolean((order as any)?.t?.trigger) ||
      /\b(stop|take\s+profit|trigger)\b/i.test(orderTypeStr);
    if (!isTrigger) continue;
    const reduceOnly =
      (order as any).reduceOnly === true || (order as any).reduce_only === true || (order as any).r === true;
    const px = parseHlOrderPx(order);
    if (!Number.isFinite(px) || px <= 0) continue;

    const fromFields = inferTpslType(order);
    let t: 'sl' | 'tp' | null = fromFields;

    const entryOk =
      ctx != null &&
      Number.isFinite(Number(ctx.entryPrice)) &&
      Number(ctx.entryPrice) > 0 &&
      (ctx.side === 'long' || ctx.side === 'short');
    if (entryOk && !orderClosesPositionSide(order, ctx!.side)) continue;

    if (t == null && entryOk) {
      t = inferTpslFromPriceVsEntry(px, Number(ctx!.entryPrice), ctx!.side);
      // Without reduceOnly, only trust heuristic if order copy looks like protection or HL left orderType blank
      if (t != null && !reduceOnly && fromFields == null) {
        const ot = orderTypeStr.trim();
        if (ot !== '' && !/\b(stop|take|profit|loss|sl|tp)\b/i.test(orderTypeStr)) {
          t = null;
        }
      }
    }

    // TP/SL triggers are reduce-only by construction; frontendOpenOrders sometimes omits the flag.
    if (!reduceOnly && fromFields == null && t == null) continue;

    if (t == null) continue;
    if (t === 'sl') slPrice = px;
    if (t === 'tp') tpPrice = px;
    if (slPrice != null && tpPrice != null) break;
  }
  return { slPrice, tpPrice };
}

/**
 * Subscribe to live portfolio updates via WebSocket.
 * Returns an unsubscribe function.
 */
export function subscribeToHLUpdates(
  walletAddress: string,
  callback: HLWebSocketCallback
): () => void {
  wsCallbacks.add(callback);
  
  // If already connected to same wallet, just add callback
  if (
    wsInstance &&
    (wsInstance.readyState === WebSocket.OPEN || wsInstance.readyState === WebSocket.CONNECTING) &&
    wsWalletAddress === walletAddress
  ) {
    return () => wsCallbacks.delete(callback);
  }

  // Close existing connection if wallet changed
  if (wsInstance && wsWalletAddress !== walletAddress) {
    wsInstance.close();
    wsInstance = null;
  }

  wsWalletAddress = walletAddress;
  connectWebSocket(walletAddress);

  return () => {
    wsCallbacks.delete(callback);
    // If no more callbacks, close connection
    if (wsCallbacks.size === 0 && wsOrderCallbacks.size === 0 && wsInstance) {
      wsInstance.close();
      wsInstance = null;
      wsWalletAddress = null;
    }
  };
}

/**
 * Subscribe to live order updates via WebSocket (`orderUpdates`).
 * Returns an unsubscribe function.
 */
export function subscribeToHLOrderUpdates(
  walletAddress: string,
  callback: HLOrderUpdateCallback
): () => void {
  wsOrderCallbacks.add(callback);

  if (
    wsInstance &&
    (wsInstance.readyState === WebSocket.OPEN || wsInstance.readyState === WebSocket.CONNECTING) &&
    wsWalletAddress === walletAddress
  ) {
    return () => wsOrderCallbacks.delete(callback);
  }

  if (wsInstance && wsWalletAddress !== walletAddress) {
    wsInstance.close();
    wsInstance = null;
  }

  wsWalletAddress = walletAddress;
  connectWebSocket(walletAddress);

  return () => {
    wsOrderCallbacks.delete(callback);
    if (wsCallbacks.size === 0 && wsOrderCallbacks.size === 0 && wsInstance) {
      wsInstance.close();
      wsInstance = null;
      wsWalletAddress = null;
    }
  };
}

function queueWsOpenOrdersRefresh(walletAddress: string, ws: WebSocket) {
  if (hlWsOpenOrdersTimer) clearTimeout(hlWsOpenOrdersTimer);
  hlWsOpenOrdersTimer = setTimeout(() => {
    hlWsOpenOrdersTimer = null;
    if (hlWsOpenOrdersCooldownTimer) {
      clearTimeout(hlWsOpenOrdersCooldownTimer);
      hlWsOpenOrdersCooldownTimer = null;
    }
    const now = Date.now();
    const gap = Math.max(0, HL_WS_OPEN_ORDERS_MIN_GAP_MS - (now - hlWsOpenOrdersLastRunMs));
    const runFetch = () => {
      hlWsOpenOrdersLastRunMs = Date.now();
      fetchHLFrontendOpenOrders(walletAddress, { forceFresh: true })
        .then((merged) => {
          if (ws !== wsInstance) return;
          try {
            const addr = String(walletAddress || '').trim().toLowerCase();
            openOrdersCache.set(addr, { ts: Date.now(), data: merged });
          } catch {}
          wsOrderCallbacks.forEach((cb) => {
            try {
              cb(merged);
            } catch {}
          });
        })
        .catch(() => {});
    };
    if (gap > 0) {
      hlWsOpenOrdersCooldownTimer = setTimeout(() => {
        hlWsOpenOrdersCooldownTimer = null;
        runFetch();
      }, gap);
    } else {
      runFetch();
    }
  }, HL_WS_OPEN_ORDERS_DEBOUNCE_MS);
}

async function connectWebSocket(walletAddress: string) {
  if (wsReconnectTimeout) {
    clearTimeout(wsReconnectTimeout);
    wsReconnectTimeout = null;
  }

  try {
    // If we already have an in-flight or open socket for this wallet, don't create another.
    if (
      wsInstance &&
      (wsInstance.readyState === WebSocket.OPEN || wsInstance.readyState === WebSocket.CONNECTING) &&
      wsWalletAddress === walletAddress
    ) {
      return;
    }

    const ws = new WebSocket('wss://api.hyperliquid.xyz/ws');
    wsInstance = ws;
    wsLiveMids.clear();
    wsLastPortfolio = null;

    ws.onopen = () => {
      console.log('[HL WS] Connected');
      try {
        if (ws !== wsInstance) return; // stale socket
        if (ws.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify({
          method: 'subscribe',
          subscription: {
            type: 'userEvents',
            user: walletAddress,
          },
        }));
        ws.send(JSON.stringify({
          method: 'subscribe',
          subscription: {
            type: 'orderUpdates',
            user: walletAddress,
          },
        }));
        ws.send(JSON.stringify({ method: 'subscribe', subscription: { type: 'allMids' } }));
        for (const dex of WS_ALL_MIDS_DEXS) {
          ws.send(JSON.stringify({ method: 'subscribe', subscription: { type: 'allMids', dex } }));
        }
      } catch (e) {
        console.warn('[HL WS] subscribe send failed:', e);
      }
      if (wsPingTimer) clearInterval(wsPingTimer);
      wsPingTimer = setInterval(() => {
        if (ws !== wsInstance || ws.readyState !== WebSocket.OPEN) return;
        try {
          ws.send(JSON.stringify({ method: 'ping' }));
        } catch {}
      }, WS_PING_MS);
      void fetchHLPortfolio(walletAddress, { forceFresh: true }).then((portfolio) => {
        if (ws !== wsInstance || !portfolio) return;
        publishHlWsPortfolio(portfolio, true);
      });
    };

    ws.onmessage = async (event) => {
      try {
        if (ws !== wsInstance) return; // stale socket
        const msg = JSON.parse(event.data);
        const channel = String(msg?.channel || '');
        if (channel === 'pong' || channel === 'subscriptionResponse') return;

        if (channel === 'allMids') {
          applyWsAllMids(msg);
          return;
        }

        if (channel === 'orderUpdates' && Array.isArray(msg.data)) {
          queueWsOpenOrdersRefresh(walletAddress, ws);
          return;
        }

        if (channel === 'userEvents' || msg.data?.fills || msg.data?.funding) {
          const now = Date.now();
          if (wsPendingPortfolioTimer) return;
          const delay = Math.max(0, WS_PORTFOLIO_MIN_INTERVAL_MS - (now - wsLastPortfolioFetchTs));
          wsPendingPortfolioTimer = setTimeout(async () => {
            wsPendingPortfolioTimer = null;
            wsLastPortfolioFetchTs = Date.now();
            const portfolio = await fetchHLPortfolio(walletAddress, { forceFresh: true });
            if (ws !== wsInstance || !portfolio) return;
            publishHlWsPortfolio(portfolio, true);
          }, delay);
        }
      } catch (err) {
        console.error('[HL WS] Message parse error:', err);
      }
    };

    ws.onerror = (err) => {
      if (ws !== wsInstance) return;
      console.error('[HL WS] Error:', err);
    };

    ws.onclose = () => {
      if (ws !== wsInstance) return;
      console.log('[HL WS] Disconnected');
      wsInstance = null;
      clearHlWsTimers();
      if (hlWsOpenOrdersTimer) {
        clearTimeout(hlWsOpenOrdersTimer);
        hlWsOpenOrdersTimer = null;
      }
      if (hlWsOpenOrdersCooldownTimer) {
        clearTimeout(hlWsOpenOrdersCooldownTimer);
        hlWsOpenOrdersCooldownTimer = null;
      }

      if (wsCallbacks.size > 0 || wsOrderCallbacks.size > 0) {
        wsReconnectTimeout = setTimeout(() => {
          if (wsWalletAddress) {
            connectWebSocket(wsWalletAddress);
          }
        }, 5000);
      }
    };
  } catch (err) {
    console.error('[HL WS] Connection error:', err);
  }
}

/**
 * Disconnect WebSocket and clean up.
 */
export function disconnectHLWebSocket() {
  if (wsReconnectTimeout) {
    clearTimeout(wsReconnectTimeout);
    wsReconnectTimeout = null;
  }
  if (hlWsOpenOrdersTimer) {
    clearTimeout(hlWsOpenOrdersTimer);
    hlWsOpenOrdersTimer = null;
  }
  if (hlWsOpenOrdersCooldownTimer) {
    clearTimeout(hlWsOpenOrdersCooldownTimer);
    hlWsOpenOrdersCooldownTimer = null;
  }
  clearHlWsTimers();
  wsLiveMids.clear();
  wsLastPortfolio = null;
  if (wsInstance) {
    wsInstance.close();
    wsInstance = null;
  }
  wsWalletAddress = null;
  wsCallbacks.clear();
}


